import { useCallback, useEffect, useMemo, useState } from 'react';
import { Edit, Plus, Power, Wrench } from 'lucide-react';
import toast from 'react-hot-toast';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import PageHeader from '../components/shared/PageHeader';
import Select from '../components/ui/Select';
import Table from '../components/ui/Table';
import { supabase } from '../lib/supabase';
import { currency } from '../utils/formatters';
import { normalizeMoney, onlyDigits } from '../utils/masks';
import { useAuth } from '../hooks/useAuth';

const emptyForm = {
  nome: '',
  categoria: '',
  descricao_padrao: '',
  preco: '',
  custo: '',
  duracao_estimada: 60,
  ativo: true,
};

const categorias = [
  'Mecânica',
  'Revisão',
  'Elétrica',
  'Freios',
  'Suspensão',
  'Alinhamento',
  'Diagnóstico',
  'Outros',
];

export default function Servicos() {
  const { perfil } = useAuth();
  const canViewFinance = perfil?.perfil === 'admin';
  const [officeId, setOfficeId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const getOfficeId = useCallback(async () => {
    const { data: authData } = await supabase.auth.getUser();
    const email = authData?.user?.email;
    if (!email) return null;
    const { data } = await supabase
      .from('usuarios')
      .select('oficina_id')
      .eq('email', email)
      .maybeSingle();
    return data?.oficina_id || null;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const oficinaId = officeId || await getOfficeId();
      setOfficeId(oficinaId);
      if (!oficinaId) return;

      const { data, error } = await supabase
        .from('servicos_oficina')
        .select('*')
        .eq('oficina_id', oficinaId)
        .order('ativo', { ascending: false })
        .order('nome');

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('Erro ao carregar serviços:', error);
      toast.error('Não foi possível carregar os serviços');
    } finally {
      setLoading(false);
    }
  }, [getOfficeId, officeId]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const active = items.filter((item) => item.ativo).length;
    const avgTicket = items.reduce((sum, item) => sum + Number(item.preco || 0), 0) / Math.max(1, items.length);
    const avgMargin = canViewFinance
      ? items.reduce((sum, item) => sum + (Number(item.preco || 0) - Number(item.custo || 0)), 0) / Math.max(1, items.length)
      : 0;
    return { active, avgTicket, avgMargin };
  }, [canViewFinance, items]);

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  }

  function openEdit(item) {
    setEditing(item);
    setForm({
      nome: item.nome || '',
      categoria: item.categoria || '',
      descricao_padrao: item.descricao_padrao || '',
      preco: item.preco || '',
      custo: item.custo || '',
      duracao_estimada: item.duracao_estimada || 60,
      ativo: item.ativo !== false,
    });
    setOpen(true);
  }

  async function save(event) {
    event.preventDefault();
    if (!officeId || !form.nome.trim()) {
      toast.error('Informe o nome do serviço.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        oficina_id: officeId,
        nome: form.nome.trim(),
        categoria: form.categoria || null,
        descricao_padrao: form.descricao_padrao.trim() || null,
        preco: normalizeMoney(form.preco),
        ...(canViewFinance ? { custo: normalizeMoney(form.custo) } : {}),
        duracao_estimada: Number(form.duracao_estimada || 60),
        ativo: Boolean(form.ativo),
      };

      const query = editing
        ? supabase.from('servicos_oficina').update(payload).eq('id', editing.id).eq('oficina_id', officeId)
        : supabase.from('servicos_oficina').insert(payload);

      const { error } = await query;
      if (error) throw error;

      toast.success(editing ? 'Serviço atualizado.' : 'Serviço cadastrado.');
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (error) {
      console.error('Erro ao salvar serviço:', error);
      toast.error('Não foi possível salvar o serviço.');
      setLoading(false);
    }
  }

  async function toggleActive(item) {
    const { error } = await supabase
      .from('servicos_oficina')
      .update({ ativo: !item.ativo })
      .eq('id', item.id)
      .eq('oficina_id', officeId);

    if (error) {
      toast.error('Não foi possível alterar o status.');
      return;
    }
    load();
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Serviços"
        subtitle="Catálogo com preço, custo e tempo para acelerar a geração de OS"
        actions={<Button onClick={openCreate}><Plus size={15} /> Novo serviço</Button>}
      />

      <div className={`grid gap-3 ${canViewFinance ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        <Card><p className="text-sm text-zinc-400">Serviços ativos</p><p className="font-display text-3xl">{stats.active}</p></Card>
        <Card><p className="text-sm text-zinc-400">Ticket médio</p><p className="font-display text-3xl text-green-400">{currency(stats.avgTicket)}</p></Card>
        {canViewFinance && <Card><p className="text-sm text-zinc-400">Margem média</p><p className="font-display text-3xl text-orange-400">{currency(stats.avgMargin)}</p></Card>}
      </div>

      <Card>
        <Table
          columns={['Serviço', 'Categoria', 'Tempo', 'Preço', ...(canViewFinance ? ['Custo', 'Margem'] : []), 'Status', 'Ações']}
          rows={items}
          loading={loading}
          emptyMessage="Cadastre seus serviços para acelerar as OS e enxergar margem."
          renderRow={(item) => (
            <tr key={item.id} className="border-t border-border hover:bg-surface2">
              <td className="px-3 py-2">
                <div className="font-medium">{item.nome}</div>
                <div className="text-xs text-zinc-500">{item.descricao_padrao || 'Sem descrição padrão'}</div>
              </td>
              <td className="px-3 py-2">{item.categoria || '-'}</td>
              <td className="px-3 py-2">{item.duracao_estimada || 60} min</td>
              <td className="px-3 py-2">{currency(item.preco)}</td>
              {canViewFinance && <td className="px-3 py-2">{currency(item.custo)}</td>}
              {canViewFinance && <td className="px-3 py-2">{currency(Number(item.preco || 0) - Number(item.custo || 0))}</td>}
              <td className="px-3 py-2"><Badge>{item.ativo ? 'ativo' : 'inativo'}</Badge></td>
              <td className="px-3 py-2">
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(item)}><Edit size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => toggleActive(item)}><Power size={14} /></Button>
                </div>
              </td>
            </tr>
          )}
        />
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? 'Editar serviço' : 'Novo serviço'}>
        <form onSubmit={save} className="space-y-4">
          <div className="flex items-center gap-2">
            <Wrench size={18} className="text-orange-400" />
            <h3 className="font-semibold">Dados do serviço</h3>
          </div>
          <Input placeholder="Nome do serviço" value={form.nome} onChange={(event) => setForm((prev) => ({ ...prev, nome: event.target.value }))} required />
          <Select value={form.categoria} onChange={(event) => setForm((prev) => ({ ...prev, categoria: event.target.value }))} options={[{ value: '', label: 'Categoria' }, ...categorias.map((item) => ({ value: item, label: item }))]} />
          <textarea
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-white focus:border-accent focus:outline-none"
            rows={3}
            placeholder="Descrição padrão para entrar automaticamente na OS"
            value={form.descricao_padrao}
            onChange={(event) => setForm((prev) => ({ ...prev, descricao_padrao: event.target.value }))}
          />
          <div className={`grid gap-3 ${canViewFinance ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
            <Input placeholder="Preço de venda" mask="money" value={form.preco} onChange={(event) => setForm((prev) => ({ ...prev, preco: normalizeMoney(event.target.value) }))} />
            {canViewFinance && <Input placeholder="Custo interno" mask="money" value={form.custo} onChange={(event) => setForm((prev) => ({ ...prev, custo: normalizeMoney(event.target.value) }))} />}
            <Input placeholder="Duração em min" mask="integer" value={form.duracao_estimada} onChange={(event) => setForm((prev) => ({ ...prev, duracao_estimada: onlyDigits(event.target.value, 4) }))} />
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={form.ativo} onChange={(event) => setForm((prev) => ({ ...prev, ativo: event.target.checked }))} />
            Serviço ativo
          </label>
          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" disabled={loading}>{loading ? 'Salvando...' : 'Salvar'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
